// @oasis-permission-inherits: /basecamp (studio-edits:view)
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Skeleton } from "~/components/ui/skeleton";
import { getAiEditStyles } from "~/modules/ai-edit-tool/network/queries";
import { OutputVideo } from "~/modules/basecamp-user-outputs/components";
import { userOutputsQuery } from "~/modules/basecamp-user-outputs/queries";
import { userOutputSearchSchema, type UserOutput } from "~/modules/basecamp-user-outputs/schemas";
import { formatRelativeTime } from "~/modules/videos/utils";

export const Route = createFileRoute("/_dash/basecamp/user-outputs/")({
  validateSearch: userOutputSearchSchema,
  component: UserOutputsPage,
});

function UserOutputsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const styles = getAiEditStyles.useQuery({
    params: { client: "DESKTOP", version: "dev", afterCursor: "", tags: [], pageSize: 300 },
  });
  const {
    data,
    error,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = userOutputsQuery.useInfiniteQuery({
    params: {
      styleId: search.styleId || undefined,
      chosenTier: search.chosenTier === "any" ? undefined : search.chosenTier,
      limit: 30,
    },
  });
  const outputs = data?.pages.flatMap((page) => page.items) ?? [];

  const styleNames = new Map(
    outputs.map((output) => [output.styleId, output.styleName || output.styleId])
  );
  for (const style of styles.data?.styles.page ?? []) {
    styleNames.set(style.id, style.displayName);
  }
  if (search.styleId && !styleNames.has(search.styleId)) {
    styleNames.set(search.styleId, search.styleId);
  }
  const styleOptions = [...styleNames].sort(([, a], [, b]) => a.localeCompare(b));

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">Basecamp user outputs</h1>
          <p className="text-sm text-muted-foreground">
            A sample of production creates that ran on Basecamp, newest first. Open one to watch and
            rate it.
          </p>
        </div>
        <Button variant="outline" size="sm" disabled={isFetching} onClick={() => void refetch()}>
          Refresh
        </Button>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label htmlFor="user-output-style">Style</Label>
          <Select
            value={search.styleId || "any"}
            onValueChange={(styleId) =>
              void navigate({ search: { ...search, styleId: styleId === "any" ? "" : styleId } })
            }
          >
            <SelectTrigger id="user-output-style" className="w-60 max-w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any style</SelectItem>
              {styleOptions.map(([id, name]) => (
                <SelectItem key={id} value={id}>
                  {name}
                </SelectItem>
              ))}
              {styles.isLoading && (
                <SelectItem value="loading" disabled>
                  Loading styles…
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="user-output-tier">Chosen tier</Label>
          <Select
            value={search.chosenTier}
            onValueChange={(chosenTier) =>
              void navigate({
                search: {
                  ...search,
                  chosenTier: userOutputSearchSchema.shape.chosenTier.parse(chosenTier),
                },
              })
            }
          >
            <SelectTrigger id="user-output-tier" className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any tier</SelectItem>
              <SelectItem value="Quick">Quick</SelectItem>
              <SelectItem value="none">No tier</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {styles.error && (
        <p role="alert" className="text-sm text-destructive">
          Failed to load the style catalog: {styles.error.message}
        </p>
      )}
      {error && (
        <p
          role="alert"
          className="rounded-md border border-destructive p-4 text-sm text-destructive"
        >
          Failed to load user outputs: {error.message}
        </p>
      )}
      {isLoading && <Skeleton className="h-64 w-full" />}
      {!isLoading && !error && outputs.length === 0 && (
        <p className="rounded-md border p-8 text-center text-muted-foreground">
          No user outputs match these filters.
        </p>
      )}
      <div className="grid items-start gap-4 lg:grid-cols-2 2xl:grid-cols-3">
        {outputs.map((output) => (
          <article
            key={output.operationId}
            className="min-w-0 space-y-3 rounded-lg border bg-card p-4"
          >
            <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
              <span title={new Date(output.createdAt).toLocaleString()}>
                {formatRelativeTime(Date.parse(output.createdAt))}
              </span>
              <Link
                to="/basecamp/user-outputs/$operationId"
                params={{ operationId: output.operationId }}
                search={search}
                className="font-medium text-foreground underline"
              >
                Open details
              </Link>
            </div>
            <div className="flex flex-wrap gap-2 text-xs">
              <Badge variant={output.status === "failed" ? "destructive" : "secondary"}>
                {output.status}
              </Badge>
              <Badge variant="outline" className="max-w-full whitespace-normal break-words">
                {output.styleName || output.styleId}
              </Badge>
              <Badge variant="outline">{output.chosenTier ?? "No tier"}</Badge>
              <Badge variant="outline">
                {output.feedback?.rating ? `Your rating: ${output.feedback.rating} ★` : "Unrated"}
              </Badge>
            </div>
            <OutputVideo
              videoUrl={output.videoUrl}
              unavailableMessage={videoUnavailableMessage(output)}
            />
            <p
              className="truncate font-mono text-xs text-muted-foreground"
              title={output.operationId}
            >
              {output.operationId}
            </p>
          </article>
        ))}
      </div>
      {hasNextPage && (
        <Button
          variant="outline"
          disabled={isFetchingNextPage}
          onClick={() => void fetchNextPage()}
        >
          {isFetchingNextPage ? "Loading…" : "Load more"}
        </Button>
      )}
    </div>
  );
}

function videoUnavailableMessage(output: UserOutput) {
  if (output.renderError) {
    return output.renderError;
  }
  if (output.status === "failed") {
    return output.error ?? "Create failed";
  }
  if (output.status === "complete") {
    return "Rendering…";
  }
  return "Processing…";
}
